import { KeyValue } from '@angular/common';
import {
  ECONOMY_CABIN,
  ECONOMY_CABIN_DESIGNATOR,
  FlowBaseUrlEnum,
  PREFERRED_SEAT_GROUP,
  PREMIUM_CABIN,
  PREMIUM_CABIN_DESIGNATOR,
  SEAT_CODE_BASSINET,
  SEAT_CODE_LEGROOM,
} from '@constants/app.constants';
import { CmsKeys } from '@constants/cms.constants';
import { DATE_FORMAT_DEFAULT } from '@constants/date-format.constants';
import { ALL_NUMBERS_REGEX } from '@constants/form.constants';
import {
  NullableString,
  SeatIconType,
  SelectedSeats,
} from '@models/app.models';

import {
  ApisAddressStatus,
  PassengerAddress,
  PassengerInfant,
} from '@models/requests/passenger-update-request.model';
import { DateTime } from 'luxon';
import {
  Passenger,
  PassengerSeat,
  RowInformation,
  SeatAvailability,
  Segment,
  UnitInformation,
  UnitType,
} from '../models/booking.models';

export const pathPartToCmsCopyName = (pathPart: string): CmsKeys => {
  const partsProperCase = pathPart.split('-').map((pathPartSegment) => {
    return `${pathPartSegment[0]?.toUpperCase()}${pathPartSegment.substring(
      1,
    )}`;
  });
  return partsProperCase.join('') as CmsKeys;
};

export const convertToUpperCamelCase = (pathPart: string): string => {
  const partsProperCase = pathPart.split('-').map((pathPartSegment) => {
    return `${pathPartSegment[0]?.toUpperCase()}${pathPartSegment.substring(
      1,
    )}`;
  });
  return partsProperCase.join('');
};

export const getRootPath = (url: string): FlowBaseUrlEnum => {
  return (
    Object.values(FlowBaseUrlEnum).find((path) => {
      return url.includes(`/${path}/`);
    }) ?? FlowBaseUrlEnum.BOOK
  );
};

export const getPathAsArray = (url: string): string[] => {
  return url.split('/');
}

export const getUnitKey = (unit: UnitInformation): string => {
  return unit.unitKey ?? `${unit.compartmentDesignator}-${unit.designator}`;
};

export const getSeatType = (
  unit: UnitInformation,
  selectedSegment: Segment | undefined,
  selectedSeats: SelectedSeats,
  bookingAssignedSeat: PassengerSeat | null,
  passengerInfant: PassengerInfant | null,
  isCheckInFlow: boolean,
): SeatIconType => {
  const activePaxHasInfantSsr = passengerInfant != null;
  const activeTab = selectedSegment?.segmentKey;

  // can't select a seat in a cabin you did not purchase the fare for
  if (
    (selectedSegment?.cabin === PREMIUM_CABIN &&
      unit.travelClassCode === ECONOMY_CABIN_DESIGNATOR) ||
    (selectedSegment?.cabin === ECONOMY_CABIN &&
      unit.travelClassCode === PREMIUM_CABIN_DESIGNATOR)
  ) {
    return SeatIconType.OccupiedOrUnavailable;
  }

  switch (unit.type) {
    case UnitType.Lavatory:
      return SeatIconType.Lavatory;
    case UnitType.LavatoryWithHandicappedFacilities:
      return SeatIconType.AccessibleLavatory;
    case UnitType.Exit:
      return SeatIconType.Exit;
    case UnitType.Wing:
      return SeatIconType.Wing;
    case UnitType.Aisle:
      return SeatIconType.Aisle;
  }

  const isSeatSelected = Object.values(
    selectedSeats?.[activeTab ?? ''] ?? {},
  ).some((seat) => seat.seatKey === unit.unitKey);
  // seat cannot be selected if already selected by another (not active) pax
  if (isSeatSelected) {
    return SeatIconType.OccupiedOrUnavailable;
  }

  const didActivePaxPayForThisSeat =
    bookingAssignedSeat?.unitKey === unit.unitKey;

  if (unit.availability !== SeatAvailability.Open) {
    if (!didActivePaxPayForThisSeat) {
      // not available if seat is not open, unless already paid for / selected by the active pax
      return SeatIconType.OccupiedOrUnavailable;
    }
    // not available but that's because this pax paid for it (or we would have
    // returned above).
    if (activePaxHasInfantSsr) {
      // not available even if they paid for it if they have an infant,
      // as there are specific bugs around this if we allow them to reselect the seat
      return SeatIconType.OccupiedOrUnavailable;
    }
  }

  // seat may not be available, but then the pax must have paid for it and doesn't
  // have an infant ssr at this point

  if (
    !isCheckInFlow &&
    !activePaxHasInfantSsr &&
    unit.properties.find(
      (prop) =>
        prop.code === SEAT_CODE_BASSINET && stringAsBooleanProp(prop.value),
    )
  ) {
    return SeatIconType.OccupiedOrUnavailable;
  }

  if (unit.group === PREFERRED_SEAT_GROUP) {
    return SeatIconType.Preferred;
  }

  if (
    unit.properties.find(
      (prop) =>
        prop.code === SEAT_CODE_LEGROOM && stringAsBooleanProp(prop.value),
    )
  ) {
    return SeatIconType.ExtraLegRoom;
  }

  return SeatIconType.Available;
};

export const getPassengerId = (passenger: Passenger): string =>
  passenger.passengerKey;

export const getPassengerContactAddress = (
  passenger: Passenger,
): PassengerAddress | undefined => {
  return passenger.addresses?.find(
    (addr) => addr.status === ApisAddressStatus.Contact,
  );
};

export const getKey = <T>(kvPair: KeyValue<string, T>): string => kvPair.key;

export const stringAsBooleanProp = (value: NullableString): boolean =>
  value !== undefined && value !== null && value.toLowerCase() !== 'false';

export const isNonEmptyList = (list: unknown): boolean =>
  typeof list === 'object' &&
  list !== null &&
  list !== undefined &&
  (list as unknown[]).length > 0;

export const areNonEmptyLists = (lists: unknown[]): boolean =>
  lists.every(isNonEmptyList);

export const hasAnyEmptyLists = (lists: unknown[]): boolean =>
  !areNonEmptyLists(lists);

export const asString = (
  value: unknown,
  culture?: NullableString,
  format?: string,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat(
      format ? format : DATE_FORMAT_DEFAULT,
      {
        locale: culture ?? undefined,
      },
    );
  } else if (typeof value === 'string') {
    return value;
  } else if (typeof value === 'number') {
    return value.toString();
  }

  return JSON.stringify(value);
};

export const dateWithTimeToDateWithoutTime = (
  date: Date,
  plusYears = 0,
  plusMonths = 0,
  plusDays = 0,
): Date =>
  new Date(
    date.getFullYear() + plusYears,
    date.getMonth() + plusMonths,
    date.getDate() + plusDays,
  );

export const createDateFromDateLike = (
  value: Date | NullableString,
  format?: string,
): Date | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }

  if (!format) {
    return undefined;
  }

  return DateTime.fromFormat(value, format).toJSDate();
};

export const createDateOnlyString = (value: DateTime) => {
  const year = value.year;
  const month = ('0' + (value.month)).slice(-2);
  const day = ('0' + value.day).slice(-2);
  return `${year}-${month}-${day}`;
}

export const extractDigits = (value: string): string => {
  return value.match(ALL_NUMBERS_REGEX)?.join('') ?? '';
};

export const stationLatLongToNumbers = (
  latStr: string,
  lonStr: string,
): {
  lat: number;
  lon: number;
} => {
  return {
    lat:
      ((latStr.endsWith('S') ? -1 : 1) *
        Number(latStr.substring(0, latStr.length - 1))) /
      10000,
    lon:
      ((lonStr.endsWith('W') ? -1 : 1) *
        Number(lonStr.substring(0, lonStr.length - 1))) /
      10000,
  };
};

export const createPhoneNumberFromParts = (
  phoneCode = '1',
  phoneNumber: string,
): string => {
  return `+${phoneCode} ${extractDigits(phoneNumber)}`;
};

export const removePhoneNumberCountryCode = (phoneNumber: NullableString) => {
  if (!phoneNumber) {
    return '';
  }

  const indexOfSpace = phoneNumber.indexOf(' ');
  if (indexOfSpace !== -1) {
    return phoneNumber.slice(indexOfSpace + 1);
  }
  return phoneNumber;
};
export const parsePhoneNumberToParts = (
  fullNumber: string,
): {
  code: string;
  number: string;
} => {
  if (!fullNumber.includes(' ')) {
    return {
      code: '',
      number: fullNumber.trim(),
    };
  }

  const parts = fullNumber.split(' ', 2);
  return {
    code: parts[0]?.replace('+', '')?.trim() ?? '',
    number: parts[1]?.trim() ?? '',
  };
};

export const getCultureCodeParts = (
  cultureCode: NullableString,
): {
  country: string;
  language: string;
} => {
  if (!cultureCode) {
    return {
      country: '',
      language: '',
    };
  }

  const parts = cultureCode.split('-') as (string | undefined)[];
  return {
    language: parts[0]?.toLowerCase() ?? '',
    country: parts[1]?.toUpperCase() ?? '',
  };
};

export enum PaymentMethodCodeEnum {
  VISA = 'VI',
  MASTERCARD = 'MC',
  AMERICAN_EXPRESS = 'AX',
  DISCOVER = 'DS',
  UATP = 'UA',
  DINERS_CLUB = 'DI',
  JCB = 'JC',
  UNION_PAY = 'UP',
  MAESTRO = 'Maestro',
}

export const PAYMENT_METHOD_FULL_REGEX: {
  [key in PaymentMethodCodeEnum]: RegExp;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]:
    /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: /^3[47][0-9]{13}$/,
  [PaymentMethodCodeEnum.VISA]: /^4[0-9]{12}(?:[0-9]{3})?$/,
  [PaymentMethodCodeEnum.DISCOVER]:
    /^65[4-9][0-9]{13}$|^64[4-9][0-9]{13}$|^6011[0-9]{12}$|^(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
  [PaymentMethodCodeEnum.JCB]: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/,
  [PaymentMethodCodeEnum.DINERS_CLUB]: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
  [PaymentMethodCodeEnum.MAESTRO]:
    /^(5018|5081|5044|5020|5038|603845|6304|6759|676[1-3]|6799|6220|504834|504817|504645)[0-9]{8,15}$/,
  [PaymentMethodCodeEnum.UATP]: /^1[\d]{15}$/,
  [PaymentMethodCodeEnum.UNION_PAY]: /^(62[0-9]{14,17})$/,
};

export const PAYMENT_METHOD_REGEX: {
  [key in PaymentMethodCodeEnum]: RegExp;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]:
    /^5[1-5]|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))/,
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: /^3[47]/,
  [PaymentMethodCodeEnum.VISA]: /^4/,
  [PaymentMethodCodeEnum.DISCOVER]:
    /^65[4-9]|^64[4-9]|^6011|^(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5]))/,
  [PaymentMethodCodeEnum.JCB]: /^(?:2131|1800|35[0-9]{3})/,
  [PaymentMethodCodeEnum.DINERS_CLUB]: /^3(?:0[0-5]|[68][0-9])/,
  [PaymentMethodCodeEnum.MAESTRO]:
    /^(5018|5081|5044|5020|5038|603845|6304|6759|676[1-3]|6799|6220|504834|504817|504645)/,
  [PaymentMethodCodeEnum.UATP]: /^1/,
  [PaymentMethodCodeEnum.UNION_PAY]: /^62/,
};

export const PAYMENT_METHOD_MAX_LENGTH: {
  [key in PaymentMethodCodeEnum]: number;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]: 16,
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: 15,
  [PaymentMethodCodeEnum.VISA]: 16,
  [PaymentMethodCodeEnum.DISCOVER]: 19,
  [PaymentMethodCodeEnum.JCB]: 19,
  [PaymentMethodCodeEnum.DINERS_CLUB]: 19,
  [PaymentMethodCodeEnum.MAESTRO]: 19,
  [PaymentMethodCodeEnum.UATP]: 15,
  [PaymentMethodCodeEnum.UNION_PAY]: 19
};

export const PAYMENT_METHOD_CVV_MAX_LENGTH: {
  [key in PaymentMethodCodeEnum]: number;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]: 3,
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: 4,
  [PaymentMethodCodeEnum.VISA]: 3,
  [PaymentMethodCodeEnum.DISCOVER]: 3,
  [PaymentMethodCodeEnum.JCB]: 3,
  [PaymentMethodCodeEnum.DINERS_CLUB]: 3,
  [PaymentMethodCodeEnum.MAESTRO]: 3,
  [PaymentMethodCodeEnum.UATP]: 3,
  [PaymentMethodCodeEnum.UNION_PAY]: 3
};

export const PAYMENT_METHOD_CVV_MIN_LENGTH: {
  [key in PaymentMethodCodeEnum]: number;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]: 3,
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: 4,
  [PaymentMethodCodeEnum.VISA]: 3,
  [PaymentMethodCodeEnum.DISCOVER]: 3,
  [PaymentMethodCodeEnum.JCB]: 3,
  [PaymentMethodCodeEnum.DINERS_CLUB]: 3,
  [PaymentMethodCodeEnum.MAESTRO]: 3,
  [PaymentMethodCodeEnum.UATP]: 3,
  [PaymentMethodCodeEnum.UNION_PAY]: 3
};

export const CREDIT_CARD_TYPE: {
  [key in PaymentMethodCodeEnum]: string;
} = {
  [PaymentMethodCodeEnum.MASTERCARD]: 'mastercard',
  [PaymentMethodCodeEnum.AMERICAN_EXPRESS]: 'american-express',
  [PaymentMethodCodeEnum.VISA]: 'visa',
  [PaymentMethodCodeEnum.DISCOVER]: 'discover',
  [PaymentMethodCodeEnum.JCB]: 'jcb',
  [PaymentMethodCodeEnum.DINERS_CLUB]: 'diners-club',
  [PaymentMethodCodeEnum.MAESTRO]: 'maestro',
  [PaymentMethodCodeEnum.UATP]: 'uatp',
  [PaymentMethodCodeEnum.UNION_PAY]: 'unionpay'
};

export const getPaymentMethodCodeFromCardNumber = (
  cardNumber: string,
  regexList: {
    [key in PaymentMethodCodeEnum]: RegExp;
  },
): PaymentMethodCodeEnum | null => {
  return (
    (Object.keys(regexList) as PaymentMethodCodeEnum[]).find(
      (paymentMethodCode) => {
        const regExp = regexList[paymentMethodCode];
        return regExp.test(cardNumber);
      },
    ) ?? null
  );
};

/**
 * Gets the designator of the first row that has a designator.
 * Will return '$' if none is found, although this condition
 * should never occur.
 * @param rows The rows for this cabin.
 * @returns The designator of the first row that has one.
 */
export const getFirstRowDesignator = (rows: RowInformation[]): string => {
  return rows.find((r) => !!r.designator)?.designator ?? '$';
};


/**
 * Sorts week numbers ascending.
 * Covers exception where luxon library returns week 53 as week 1
 */
export const luxonWeeksSort = (weekA: number, weekB: number) => {
  // december sort where week 53 in luxon is marked as week 1 (Chrome, Egde, Safari, Opera)
  if ( weekA >= 48 && weekB === 1 ) {
    return weekB - weekA
  }
  // december sort where week 53 in luxon is marked as week 1 (Firefox)
  if (weekA === 1 && weekB >= 48) {
    return 1;
  }
  // standard sort
  return weekA - weekB
}
